import { reauthenticateBankConnection } from "@/apis/custom/customRoutes";
import type { BankConnection } from "@/apis/gql/types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/providers/session";
import { useGetAllBankConnections } from "@/queries/bank-connections/get-all";
import { useParams } from "@tanstack/react-router";
import { AlertCircleIcon, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Props = {
  className?: string;
  onlyForBankConnectionId?: string;
};

export function BankReauthAlert({ className, onlyForBankConnectionId }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { company } = useParams({ strict: false });
  const { tenantId } = useTenant();

  const {
    bankConnections,
    loading: bankConnsLoading,
    refetch,
  } = useGetAllBankConnections({ fetchPolicy: "cache-and-network" });

  const relevantBankConnections = onlyForBankConnectionId
    ? bankConnections?.filter(
        (c) => c.bankConnectionId === onlyForBankConnectionId,
      )
    : bankConnections;

  const hasReauthRequired = relevantBankConnections?.some(
    (c) => c.reauthRequired,
  );
  const hasFintsInterfaceRequired = relevantBankConnections?.some(
    (c) => c.shouldAddFints,
  );
  const connectionNeedingReauth =
    relevantBankConnections?.find((c) => c.reauthRequired) ?? null;

  const bankConnectionNeedingFintsInterface =
    relevantBankConnections?.find((c) => c.shouldAddFints) ?? null;

  const shouldShowFintsWarning =
    !bankConnsLoading && !hasReauthRequired && hasFintsInterfaceRequired;
  const shouldShowReauthAlert =
    !bankConnsLoading && connectionNeedingReauth && hasReauthRequired;

  if (shouldShowFintsWarning) {
    return (
      <FinTsWarning
        className={className}
        loading={loading}
        handleReauthenticate={handleReauthenticate}
        bankConnectionNeedingFintsInterface={
          bankConnectionNeedingFintsInterface
        }
      />
    );
  }

  if (shouldShowReauthAlert)
    return (
      <ReauthAlert
        className={className}
        loading={loading}
        handleReauthenticate={handleReauthenticate}
        connectionNeedingReauth={connectionNeedingReauth}
      />
    );

  return null;

  async function handleReauthenticate(
    bankConnection: BankConnection | null,
    forceFintsInterface = false,
  ) {
    if (!bankConnection || !tenantId) return;
    try {
      setLoading(true);
      const response = await reauthenticateBankConnection(
        bankConnection.bankConnectionId,
        tenantId,
        forceFintsInterface ? true : undefined,
      );
      if (response.status !== 200) {
        toast.error(t("bank_connections.toasts.reauth_error"));
        refetch();
        return;
      }
      const { webformUrl } = response.data ?? {};
      if (!webformUrl) {
        toast.info(t("bank_connections.toasts.no_reauth_needed"));
        refetch();
        return;
      }
      const companySlug = company as string;
      const baseRedirectUrl = `${window.location.origin}/${companySlug}/unternehmen/einstellungen/bank`;
      const successRedirectUrl = encodeURIComponent(
        `${baseRedirectUrl}?reauthSuccess=true`,
      );
      const errorRedirectUrl = encodeURIComponent(
        `${baseRedirectUrl}?reauthError=true`,
      );
      const abortRedirectUrl = encodeURIComponent(
        `${baseRedirectUrl}?reauthAbort=true`,
      );
      window.location.href = `${webformUrl}?redirectUrl=${successRedirectUrl}&errorRedirectUrl=${errorRedirectUrl}&abortRedirectUrl=${abortRedirectUrl}`;
    } finally {
      setLoading(false);
    }
  }
}

function ReauthAlert({
  className,
  loading,
  handleReauthenticate,
  connectionNeedingReauth,
}: {
  className: string | undefined;
  loading: boolean;
  handleReauthenticate: (
    bankConnection: BankConnection | null,
    forceFintsInterface?: boolean,
  ) => Promise<void>;
  connectionNeedingReauth: BankConnection;
}) {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <Alert
        variant="warning"
        className="flex h-full items-center justify-between whitespace-normal"
      >
        <div className="flex gap-2">
          <AlertCircleIcon className="w-6 h-6" />
          <div>
            <AlertTitle className="font-medium">
              {t("bank_connections.reauth_alert.title")}
            </AlertTitle>
            <AlertDescription className="text-xs">
              {t("bank_connections.reauth_alert.description")}
            </AlertDescription>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="ml-3"
            onClick={() => handleReauthenticate(connectionNeedingReauth)}
          >
            {t("bank_connections.reauth_alert.button")}
          </Button>
        )}
      </Alert>
    </div>
  );
}

function FinTsWarning({
  className,
  loading,
  handleReauthenticate,
  bankConnectionNeedingFintsInterface,
}: {
  className: string | undefined;
  loading: boolean;
  handleReauthenticate: (
    bankConnection: BankConnection | null,
    forceFintsInterface?: boolean,
  ) => Promise<void>;
  bankConnectionNeedingFintsInterface: BankConnection | null;
}) {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <Alert
        variant="warning"
        className="flex h-full items-center justify-between whitespace-normal"
      >
        <div className="flex gap-2">
          <AlertCircleIcon className="w-6 h-6" />
          <div>
            <AlertTitle className="font-medium">
              {t("bank_connections.fints_warning.title")}
            </AlertTitle>
            <AlertDescription className="text-xs">
              {t("bank_connections.fints_warning.short_description")}
            </AlertDescription>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="ml-3"
            onClick={() =>
              handleReauthenticate(bankConnectionNeedingFintsInterface, true)
            }
          >
            {t("bank_connections.fints_warning.button")}
          </Button>
        )}
      </Alert>
    </div>
  );
}
